import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useLocale } from '../i18n/LocaleContext'
import { getLocalizedPath } from '../utils/locale'

interface SEOHeadProps {
  title?: string
  description?: string
  noindex?: boolean
}

export default function SEOHead({ title, description, noindex }: SEOHeadProps = {}) {
  const { locale, t, otherLocale } = useLocale()
  const { pathname } = useLocation()

  const pageTitle = title || t.meta.siteTitle
  const pageDescription = description || t.meta.siteDescription

  useEffect(() => {
    const baseUrl = window.location.origin

    // Set title
    document.title = pageTitle

    // Set or update meta description
    let metaDesc = document.querySelector('meta[name="description"]')
    if (!metaDesc) {
      metaDesc = document.createElement('meta')
      metaDesc.setAttribute('name', 'description')
      document.head.appendChild(metaDesc)
    }
    metaDesc.setAttribute('content', pageDescription)

    // Robots meta: noindex on transactional pages (registration/payment).
    setMetaName('robots', noindex ? 'noindex, nofollow' : 'index, follow')

    // OG tags
    setMetaProperty('og:title', pageTitle)
    setMetaProperty('og:description', pageDescription)
    setMetaProperty('og:type', 'website')
    setMetaProperty('og:url', `${baseUrl}${pathname}`)
    setMetaProperty('og:locale', locale === 'hu' ? 'hu_HU' : 'ro_RO')

    // og:image + Twitter card (only when an OG image is configured)
    if (t.meta.ogImage) {
      const imageUrl = `${baseUrl}${t.meta.ogImage}`
      setMetaProperty('og:image', imageUrl)
      setMetaName('twitter:card', 'summary_large_image')
      setMetaName('twitter:image', imageUrl)
      setMetaName('twitter:title', pageTitle)
      setMetaName('twitter:description', pageDescription)
    }

    // Canonical link
    setCanonical(`${baseUrl}${pathname}`)

    // hreflang tags
    const otherPath = getLocalizedPath(pathname, otherLocale)
    setHreflang(locale === 'hu' ? 'hu' : 'ro', `${baseUrl}${pathname}`)
    setHreflang(otherLocale === 'hu' ? 'hu' : 'ro', `${baseUrl}${otherPath}`)

    return () => {
      // Cleanup hreflang + canonical tags on unmount
      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove())
      document.querySelector('link[rel="canonical"]')?.remove()
      // Reset robots so a noindex page does not leak into the next route.
      document.querySelector('meta[name="robots"]')?.remove()
    }
  }, [locale, pathname, t, otherLocale, pageTitle, pageDescription, noindex])

  return null
}

function setMetaProperty(property: string, content: string) {
  let el = document.querySelector(`meta[property="${property}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('property', property)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function setMetaName(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('name', name)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function setCanonical(href: string) {
  let el = document.querySelector('link[rel="canonical"]')
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', 'canonical')
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

function setHreflang(lang: string, href: string) {
  let el = document.querySelector(`link[rel="alternate"][hreflang="${lang}"]`)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', 'alternate')
    el.setAttribute('hreflang', lang)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}
